import {
  Avatar,
  AvatarGroup,
  Box,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ProtectedLink from 'components/ProtectedLink';
import './OrderCard.scss';
import ToggleButton from 'components/ToggleButton';
import { trim } from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressBar } from '../../../components';
import LockIcon from '@mui/icons-material/Lock';
import { HistogramIcon, EditIcon } from '../../../components/CustomIcons';
import { capitalize, numberWithSpaces, stringToColor } from '../../../helpers/format';
import useManagementRights from '../../../hooks/rights/useManagementRights';
import useUserRoles from '../../../hooks/useUserRoles';
import { useSelector } from '../../../store/hooks';
import useManagementHelper from '../../Management/services/useManagementHelper';
import FinancialGraph from '../OrderGraphs/FinancialGraph/FinancialGraph';
import { useLocation } from 'react-router-dom';
import OrderKpi from '../OrderKpi/OrderKpi';
import OrderMenu from '../OrderMenu/OrderMenu';
import { OrderCardProps } from './OrderCard';
import useOrderCardService from './useOrderCardService';
import { OrderType } from 'types/enums/OrderType';
import moment from 'moment';
import { useAppState } from 'store/Provider';
import { setActiveLastOrderIdAction, setActiveScrollOrderIdAction } from 'store/actions/managementActions';
import useManagementService from 'features/Management/services/useManagementService';

const OrderProductionCardContent: React.FC<OrderCardProps> = ({
  order,
  menu = true,
  stats = true,
  graph = true,
  kpi = true,
  isFromMain = false,
  showLink = true,
}) => {
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const location = useLocation();
  const { app, management } = useSelector((state) => state);
  const { canShowStatOrder, canShowGraphOrder, canShowKpiOrder } = useManagementRights(order);
  const { baselineEngagementParaPage } = useManagementService();
  const userRoles = useUserRoles();
  const { engagementDate, orderConsultantTooltipTitle, orderClient } = useOrderCardService(order);
  const { getColorDisplayed } = useManagementHelper();
  const pathname = `/${app.customer?.slug}/deliverables/${order.id}`;
  const [isKpiOpen, setIsKpiOpen] = useState(false);
  const [isGraphOpen, setIsGraphOpen] = useState(false);
  const devises = useSelector((state) => state.app.devises);
  const currentDevise = devises?.find((devise) => order.devise_name[0] === devise.name)?.symbol;

  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const userTimeZone = moment.tz.guess();

  const formatedCreatedDate = moment.utc(order.created_at).tz(userTimeZone).format(dateFormat);
  const formatedUpdatedDate = moment.utc(order.updated_at).tz(userTimeZone).format(dateFormat);

  const baseLineFlag = order?.is_baselined && location.pathname.includes('orders/production');

  const paragraphRef = useRef<HTMLDivElement>(null);

  const handleLinkClick = (e: any) => {
    if (!order.is_baselined) {
      dispatch(setActiveLastOrderIdAction(order.id));
      const topOffset = paragraphRef?.current?.offsetTop ?? 0; // Default to 0 if undefined
      dispatch(setActiveScrollOrderIdAction(topOffset < 750 ? topOffset : topOffset));
    }
    // Navigate programmatically
  };

  useEffect(
    () => () => {
      if (
        management.offsetTop > 0 &&
        location.pathname.includes('orders/production') &&
        paragraphRef?.current?.offsetTop !== undefined
      ) {
        window.scrollTo({
          top: management.offsetTop - 10,
          behavior: 'smooth',
        });
      }
    },
    [management.offsetTop]
  );

  const gotoBaseline = () => {
    baselineEngagementParaPage(order);
  };
  return (
    <>
      <CardContent sx={{ position: 'relative' }} ref={paragraphRef}>
        <Grid
          item
          direction="row"
          xs={12}
          md={6}
          lg={6}
          sx={{ display: 'flex', paddingBottom: '8px' }}
          justifyContent="space-between"
        >
          <Grid item xs={12} md={6} lg={6} onClick={handleLinkClick}>
            {showLink && !order.is_baselined ? (
              <ProtectedLink pathname={pathname}>
                <Grid sx={{ display: 'flex' }}>
                  <Tooltip
                    title={
                      trim(
                        [order?.name_spec, order?.ref_spec]
                          .filter(Boolean) // remove empty/falsy values
                          .join(' - ')
                      ) ?? t<string>('Not_defined')
                    }
                  >
                    <Typography
                      noWrap
                      className="order-card-title"
                      sx={
                        management.activeLastOrderId !== null &&
                        management.activeLastOrderId === order?.id &&
                        location.pathname.includes('orders/production')
                          ? { color: '#FF00FF' }
                          : undefined
                      }
                    >
                      ID {order?.id} - {order?.name_spec}
                    </Typography>
                  </Tooltip>
                  {baseLineFlag && (
                    <Tooltip title={t<string>('Change baseline in progress.')}>
                      <LockIcon />
                    </Tooltip>
                  )}
                </Grid>
              </ProtectedLink>
            ) : (
              <Grid sx={{ display: 'flex' }}>
                <Tooltip
                  title={
                    trim(
                      [order?.name_spec, order?.ref_spec]
                        .filter(Boolean) // remove empty/falsy values
                        .join(' - ')
                    ) ?? t<string>('Not_defined')
                  }
                >
                  <Typography noWrap className="order-card-title">
                    ID {order?.id} - {order?.name_spec}
                  </Typography>
                </Tooltip>
                {baseLineFlag && (
                  <Tooltip title={t<string>('Change baseline in progress.')}>
                    <LockIcon />
                  </Tooltip>
                )}
              </Grid>
            )}

            <Typography className="order-card-label">{t('Project_name')}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Stack spacing={2} direction="row" alignItems="center">
              {baseLineFlag &&
                !userRoles.isBu_support &&
                !userRoles.isClient &&
                !userRoles.isConsultant &&
                !userRoles.isBu_support &&
                !userRoles.isDeliveryCoordinator && (
                  <IconButton
                    color="primary"
                    onClick={() => {
                      gotoBaseline();
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                    <Typography noWrap sx={{ color: 'primary.main', fontWeight: 500, textDecoration: 'underline' }}>
                      {t('edit_baseline')}
                    </Typography>
                  </IconButton>
                )}
              {menu && <OrderMenu order={order} />}
            </Stack>
          </Grid>
        </Grid>
        <Grid container spacing={1} justifyContent="space-between">
          <Grid item xs={12} md={12}>
            <Grid container spacing={1} justifyContent="space-between" alignItems="center">
              <Grid item xs={12} md={6} lg={!userRoles.isConsultant && !userRoles.isCustomer ? 1.5 : 2}>
                {order.affair ? (
                  <Tooltip title={trim([order.affair?.otp, order.affair?.name].join(' - '))}>
                    <div>
                      <Typography noWrap className="order-card-value">
                        {order.affair?.name}
                      </Typography>
                      <Typography noWrap className="order-card-label-bold">
                        {order.affair?.otp}
                      </Typography>
                    </div>
                  </Tooltip>
                ) : (
                  <div>
                    <Typography noWrap>{t('Affair_not_indicated')} </Typography>
                    <Box sx={{ height: '1.6rem' }} />
                  </div>
                )}
              </Grid>
              <Grid item xs={12} md={6} lg={!userRoles.isConsultant && !userRoles.isCustomer ? 1.5 : 2}>
                <Tooltip title={orderClient()}>
                  <Typography noWrap className="order-card-value">
                    {orderClient()}
                  </Typography>
                </Tooltip>
                <Typography noWrap className="order-card-label">
                  {t('Client')}
                </Typography>
              </Grid>
              {!userRoles.isConsultant && !userRoles.isCustomer && (
                <Grid item xs={12} md={6} lg={2}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Tooltip placement="bottom-start" title={orderConsultantTooltipTitle}>
                      {order.consultants && order.consultants[0] ? (
                        <AvatarGroup max={3}>
                          {order.consultants.map((c) =>
                            c.avatar_uri ? (
                              <Avatar src={c.avatar_uri} key={c.id} />
                            ) : (
                              <Avatar
                                sx={{
                                  backgroundColor: stringToColor(c.first_name),
                                  color: stringToColor(c.first_name, 'color'),
                                  width: 30,
                                  height: 30,
                                }}
                                key={c.id}
                              >{`${capitalize(c.first_name.charAt(0))}${capitalize(c.last_name.charAt(0))}`}</Avatar>
                            )
                          )}
                        </AvatarGroup>
                      ) : (
                        <div>
                          <Typography className="order-card-value">-</Typography>
                        </div>
                      )}
                    </Tooltip>
                    <Typography className="order-card-label">{t('Consultants')}</Typography>
                    <Box sx={{ height: '0.4rem' }} />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={6} lg={2}>
                <Tooltip title={engagementDate()}>
                  <Typography noWrap className="order-card-value">
                    {engagementDate()}
                  </Typography>
                </Tooltip>
                <Typography className="order-card-label">{t('Engagement_date')}</Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={1}>
                <Tooltip title={formatedCreatedDate}>
                  <Typography noWrap className="order-card-value">
                    {formatedCreatedDate}
                  </Typography>
                </Tooltip>
                <Typography className="order-card-label">{t('Created on')}</Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={1}>
                <Tooltip title={formatedUpdatedDate}>
                  <Typography noWrap className="order-card-value-sm">
                    {formatedUpdatedDate}
                  </Typography>
                </Tooltip>
                <Typography className="order-card-label">{t('Modified on')}</Typography>
              </Grid>
              <>
                {graph && canShowGraphOrder && (
                  <>
                    <Grid item lg={1} />
                    <Grid item xs={12} md={6} lg={1.5}>
                      <Stack spacing={2} direction="row" alignItems="center">
                        <IconButton
                          aria-label="open-graph"
                          onClick={() => setIsGraphOpen(!isGraphOpen)}
                          color="primary"
                          size="large"
                        >
                          <HistogramIcon />
                        </IconButton>
                      </Stack>
                    </Grid>
                  </>
                )}
              </>
              <Grid
                item
                xs={12}
                md={6}
                lg={0.35}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingRight: '8px' }}
              >
                <Stack spacing={2} direction="row">
                  {kpi && canShowKpiOrder && (
                    <ToggleButton
                      isOpen={isKpiOpen}
                      handleClick={() => setIsKpiOpen(!isKpiOpen)}
                      disabled={order?.is_baselined}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      {stats && canShowStatOrder && (
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={{ xs: 3, lg: 4 }} alignItems="center">
            <Grid item xs={12} md={6} lg={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {!userRoles.isCustomer &&
                    `${numberWithSpaces(order.realised_deliverables_price)} / ${numberWithSpaces(
                      order?.amount_price
                    )}  ${currentDevise && currentDevise !== 'E' ? currentDevise.toLowerCase() : '€'}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{`${
                  order.percentage_of_realised_deliverables ? order.percentage_of_realised_deliverables.toFixed(2) : 0
                }%`}</Typography>
              </Box>
              <Box sx={{ width: '100%', mt: 0.5 }}>
                <ProgressBar
                  value={order.percentage_of_realised_deliverables || 0}
                  color={getColorDisplayed(order.percentage_of_realised_deliverables)}
                  height={8}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
                {t('Realised')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {!userRoles.isCustomer &&
                    `${numberWithSpaces(order.accepted_deliverables_price)} / ${numberWithSpaces(
                      order?.amount_price || 0
                    )}  ${currentDevise && currentDevise !== 'E' ? currentDevise.toLowerCase() : '€'}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{`${
                  order.percentage_of_accepted_deliverables ? order.percentage_of_accepted_deliverables.toFixed(2) : 0
                }%`}</Typography>
              </Box>

              <Box sx={{ width: '100%', mt: 0.5 }}>
                <ProgressBar
                  value={order.percentage_of_accepted_deliverables || 0}
                  color={getColorDisplayed(order.percentage_of_accepted_deliverables)}
                  height={8}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
                {t('Accepted')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      )}
      {graph && canShowGraphOrder && (
        <Collapse in={isGraphOpen} timeout={500} unmountOnExit>
          <CardContent sx={{ p: 0 }}>
            <FinancialGraph order_id={order?.id} orderPrice={order?.price || 1} />
          </CardContent>
        </Collapse>
      )}
      {kpi && canShowKpiOrder && (
        <Collapse in={isKpiOpen} timeout={500} unmountOnExit>
          <CardContent sx={{ p: 0 }}>
            <OrderKpi order={order} isFromMain={isFromMain} />
          </CardContent>
        </Collapse>
      )}
    </>
  );
};

export default OrderProductionCardContent;
